import { create } from 'zustand'

export interface MeetingParticipant {
  identity: string
  name: string
  isMuted: boolean
  isCameraOff: boolean
  isScreenSharing: boolean
  isSpeaking: boolean
  isLocal: boolean
}

interface MeetingState {
  // Room
  roomId: string | null
  roomName: string | null
  isConnected: boolean
  isConnecting: boolean
  connectionError: string | null

  // Participants
  participants: MeetingParticipant[]
  localParticipantId: string | null

  // Local Media State
  isMuted: boolean
  isCameraOff: boolean
  isScreenSharing: boolean
  selectedAudioDevice: string | null
  selectedVideoDevice: string | null

  // UI State
  showParticipants: boolean
  displayName: string

  // Actions
  setRoom: (roomId: string, roomName: string) => void
  clearRoom: () => void
  setConnected: (connected: boolean) => void
  setConnecting: (connecting: boolean) => void
  setConnectionError: (error: string | null) => void
  setParticipants: (participants: MeetingParticipant[]) => void
  setLocalParticipantId: (id: string | null) => void
  setMuted: (muted: boolean) => void
  setCameraOff: (cameraOff: boolean) => void
  setScreenSharing: (sharing: boolean) => void
  setSelectedAudioDevice: (deviceId: string | null) => void
  setSelectedVideoDevice: (deviceId: string | null) => void
  setShowParticipants: (show: boolean) => void
  setDisplayName: (name: string) => void
  reset: () => void
}

const initialState = {
  roomId: null,
  roomName: null,
  isConnected: false,
  isConnecting: false,
  connectionError: null,
  participants: [],
  localParticipantId: null,
  isMuted: false,
  isCameraOff: false,
  isScreenSharing: false,
  selectedAudioDevice: null,
  selectedVideoDevice: null,
  showParticipants: false,
  displayName: '',
}

export const useMeetingStore = create<MeetingState>((set) => ({
  ...initialState,

  setRoom: (roomId, roomName) => set({ roomId, roomName }),

  clearRoom: () => set({
    roomId: null,
    roomName: null,
    isConnected: false,
    isConnecting: false,
    connectionError: null,
    participants: [],
    localParticipantId: null,
  }),

  setConnected: (isConnected) => set({ isConnected, isConnecting: false }),

  setConnecting: (isConnecting) => set({ isConnecting }),

  setConnectionError: (connectionError) => set({ connectionError, isConnecting: false }),

  setParticipants: (participants) => set({ participants }),

  setLocalParticipantId: (localParticipantId) => set({ localParticipantId }),

  setMuted: (isMuted) => set({ isMuted }),

  setCameraOff: (isCameraOff) => set({ isCameraOff }),

  setScreenSharing: (isScreenSharing) => set({ isScreenSharing }),

  setSelectedAudioDevice: (selectedAudioDevice) => set({ selectedAudioDevice }),

  setSelectedVideoDevice: (selectedVideoDevice) => set({ selectedVideoDevice }),

  setShowParticipants: (showParticipants) => set({ showParticipants }),

  setDisplayName: (displayName) => set({ displayName }),

  reset: () => set(initialState),
}))

// Selector hooks for convenience
export const useRoomId = () => useMeetingStore((state) => state.roomId)
export const useIsConnected = () => useMeetingStore((state) => state.isConnected)
export const useIsConnecting = () => useMeetingStore((state) => state.isConnecting)
export const useParticipants = () => useMeetingStore((state) => state.participants)
export const useDisplayName = () => useMeetingStore((state) => state.displayName)
